import { generatePath } from 'react-router-dom';
import { ApiCacheTags, baseApiService } from '@qapline/api';
import { DEFAULT_CONFIG } from '@qapline/config';
import { z } from 'zod';

import { customerSchema, TCustomer } from '../schemas';
import {
    TGetCustomerProfilePictureRequestBody,
    TGetCustomerProfilePictureResponseBody,
    TListCustomersRequestQuery,
} from '../types';

const {
    getSelf: getSelfEndpoint,
    profilePicture: profilePictureEndpoint,
    get: getEndpoint,
    list: listEndpoint,
} = DEFAULT_CONFIG.api.endpoints.customer;
const { item, profilePicture: profilePictureTag, list } = ApiCacheTags.customer;

export const customerApi = baseApiService.injectEndpoints({
    endpoints: (builder) => ({
        listCustomers: builder.query<{ data: TCustomer[]; count: number }, TListCustomersRequestQuery>({
            query: ({ filters, limit, offset, order, search }) => ({
                url: listEndpoint,
                method: 'GET',
                params: {
                    limit,
                    offset,
                    search,
                    order: order ? JSON.stringify(order) : undefined,
                    filters: filters ? JSON.stringify(filters) : undefined,
                },
            }),
            providesTags: (res, error) => (error ? [] : [{ type: list }]),
            transformResponse: (data) =>
                z
                    .object({
                        data: customerSchema.array(),
                        count: z.number(),
                    })
                    .parse(data),
        }),
        getCustomer: builder.query<TCustomer, number>({
            query: (customerId) => ({
                url: generatePath(getEndpoint, { customerId: customerId.toString() }),
                method: 'GET',
            }),
            providesTags: (res, error) => (error ? [] : [{ type: item, id: res?.customer_id }]),
            transformResponse: (data) => customerSchema.parse(data),
        }),
        getCustomerSelf: builder.query<TCustomer, void>({
            query: () => ({
                url: getSelfEndpoint,
                method: 'GET',
            }),
            providesTags: (res, error) => (error ? [] : [{ type: item, id: res?.customer_id }]),
            transformResponse: (data) => customerSchema.parse(data),
        }),
        getCustomerProfilePicture: builder.query<
            TGetCustomerProfilePictureResponseBody,
            TGetCustomerProfilePictureRequestBody
        >({
            query: ({ customerId }) => {
                return {
                    url: generatePath(profilePictureEndpoint, { customerId: customerId.toString() }),
                    method: 'GET',
                    responseHandler: async (response) => response,
                };
            },
            transformResponse: async (response: Response) => {
                const data = URL.createObjectURL(await response.blob());
                const headers = response.headers;
                return { data, headers };
            },
            providesTags: (_res, _error, { customerId }) => [{ type: profilePictureTag, id: customerId }],
        }),
    }),
});

export const {
    useGetCustomerSelfQuery,
    useLazyGetCustomerSelfQuery,
    useGetCustomerProfilePictureQuery,
    useLazyGetCustomerProfilePictureQuery,
    useGetCustomerQuery,
    useLazyListCustomersQuery,
    useListCustomersQuery,
    useLazyGetCustomerQuery,
} = customerApi;
